// src/admin/pages/AdminStats.js
import React, { useState, useEffect, useCallback } from 'react';
import { showToast } from '../../contexts/ToastNotification';
import axios from 'axios';
import './admin-stats.scss';

const API_URL = process.env.REACT_APP_API_URL || '/api';

function AdminStats() {
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState({
        totalOrders: 0,
        totalRevenue: 0,
        totalUsers: 0,
        pendingOrders: 0,
        processingOrders: 0,
        shippedOrders: 0,
        deliveredOrders: 0,
        cancelledOrders: 0,
        averageOrderValue: 0,
        topProducts: [],
        recentOrders: [],
        monthlyStats: []
    });
    const [dateRange, setDateRange] = useState('year'); // week, month, year

    // Carica statistiche
    const loadStats = useCallback(async () => {
        setLoading(true);
        try {
            // ✅ Convertito a axios
            const response = await axios.get(
                `${API_URL}/admin/orders/stats?range=${dateRange}`,
                { withCredentials: true }
            );
            const data = response.data;
            
            if (data.success) {
                setStats(data.stats);
            } else {
                showToast('Errore nel caricamento delle statistiche', 'error');
                // Dati di esempio per sviluppo
                setStats({
                    totalOrders: 156,
                    totalRevenue: 28450,
                    totalUsers: 342,
                    pendingOrders: 12,
                    processingOrders: 8,
                    shippedOrders: 45,
                    deliveredOrders: 89,
                    cancelledOrders: 10,
                    averageOrderValue: 182.5,
                    topProducts: [
                        { name: 'Montepulciano d\'Abruzzo', sales: 45, revenue: 1125 },
                        { name: 'Trebbiano d\'Abruzzo', sales: 38, revenue: 798 },
                        { name: 'Cerasuolo d\'Abruzzo', sales: 32, revenue: 896 },
                        { name: 'Pecorino', sales: 28, revenue: 840 },
                        { name: 'Passerina', sales: 25, revenue: 625 }
                    ],
                    recentOrders: [
                        { id: 1001, customer: 'Mario Rossi', amount: 156.00, date: '2024-01-15', status: 'delivered' },
                        { id: 1002, customer: 'Laura Bianchi', amount: 89.50, date: '2024-01-14', status: 'shipped' },
                        { id: 1003, customer: 'Giuseppe Verdi', amount: 234.00, date: '2024-01-14', status: 'processing' },
                        { id: 1004, customer: 'Anna Neri', amount: 67.80, date: '2024-01-13', status: 'delivered' },
                        { id: 1005, customer: 'Marco Rossi', amount: 123.40, date: '2024-01-13', status: 'pending' }
                    ],
                    monthlyStats: [
                        { month: 'Gen', orders: 42, revenue: 8450 },
                        { month: 'Feb', orders: 38, revenue: 7200 },
                        { month: 'Mar', orders: 45, revenue: 8900 },
                        { month: 'Apr', orders: 52, revenue: 10200 },
                        { month: 'Mag', orders: 48, revenue: 9600 },
                        { month: 'Giu', orders: 56, revenue: 11200 }
                    ]
                });
            }
        } catch (error) {
            console.error('Error loading stats:', error);
            showToast('Errore di connessione', 'error');
        } finally {
            setLoading(false);
        }
    }, [dateRange]);

    useEffect(() => {
        loadStats();
    }, [loadStats]);

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('it-IT', {
            style: 'currency',
            currency: 'EUR'
        }).format(value);
    };

    const getStatusBadge = (status) => {
        const badges = {
            pending: { label: '📝 In attesa', color: '#ffc107', bg: '#fff3cd' },
            processing: { label: '⚙️ In elaborazione', color: '#17a2b8', bg: '#d1ecf1' },
            shipped: { label: '🚚 Spedito', color: '#007bff', bg: '#cce5ff' },
            delivered: { label: '✅ Consegnato', color: '#28a745', bg: '#d4edda' },
            cancelled: { label: '❌ Cancellato', color: '#dc3545', bg: '#f8d7da' }
        };
        return badges[status] || { label: status, color: '#6c757d', bg: '#e9ecef' };
    };

    if (loading) {
        return (
            <div className="admin-loading">
                <div className="loader"></div>
                <span>Caricamento statistiche...</span>
            </div>
        );
    }

    return (
        <div className="admin-stats">
            {/* Header */}
            <div className="stats-header">
                <div className="header-title">
                    <h2>📈 Statistiche e Report</h2>
                    <p>Analizza le performance del tuo e-commerce e monitora le vendite</p>
                </div>
                <div className="date-range-selector">
                    <button 
                        className={dateRange === 'week' ? 'active' : ''}
                        onClick={() => setDateRange('week')}
                    >
                        📅 Settimana
                    </button>
                    <button 
                        className={dateRange === 'month' ? 'active' : ''}
                        onClick={() => setDateRange('month')}
                    >
                        📆 Mese
                    </button>
                    <button 
                        className={dateRange === 'year' ? 'active' : ''}
                        onClick={() => setDateRange('year')}
                    >
                        📊 Anno
                    </button>
                </div>
            </div>

            {/* KPI Cards */}
            <div className="kpi-grid">
                <div className="kpi-card">
                    <div className="kpi-icon">💰</div>
                    <div className="kpi-value">{formatCurrency(stats.totalRevenue)}</div>
                    <div className="kpi-label">Fatturato Totale</div>
                    <div className="kpi-trend">+12% rispetto al periodo precedente</div>
                </div>
                <div className="kpi-card">
                    <div className="kpi-icon">📦</div>
                    <div className="kpi-value">{stats.totalOrders}</div>
                    <div className="kpi-label">Ordini Totali</div>
                    <div className="kpi-trend">+8% rispetto al periodo precedente</div>
                </div>
                <div className="kpi-card">
                    <div className="kpi-icon">👥</div>
                    <div className="kpi-value">{stats.totalUsers}</div>
                    <div className="kpi-label">Utenti Registrati</div>
                    <div className="kpi-trend">+15 nuovi questa settimana</div>
                </div>
                <div className="kpi-card">
                    <div className="kpi-icon">⭐</div>
                    <div className="kpi-value">{formatCurrency(stats.averageOrderValue)}</div>
                    <div className="kpi-label">Valore Medio Ordine</div>
                    <div className="kpi-trend">+5% rispetto al periodo precedente</div>
                </div>
            </div>

            {/* Orders by Status */}
            <div className="stats-section">
                <h3>📊 Distribuzione Ordini per Stato</h3>
                <div className="status-distribution">
                    <div className="status-bar">
                        {stats.pendingOrders > 0 && (
                            <div className="bar-item pending" style={{ width: `${(stats.pendingOrders / stats.totalOrders) * 100}%` }}>
                                <span>📝 In attesa ({stats.pendingOrders})</span>
                            </div>
                        )}
                        {stats.processingOrders > 0 && (
                            <div className="bar-item processing" style={{ width: `${(stats.processingOrders / stats.totalOrders) * 100}%` }}>
                                <span>⚙️ In elaborazione ({stats.processingOrders})</span>
                            </div>
                        )}
                        {stats.shippedOrders > 0 && (
                            <div className="bar-item shipped" style={{ width: `${(stats.shippedOrders / stats.totalOrders) * 100}%` }}>
                                <span>🚚 Spedito ({stats.shippedOrders})</span>
                            </div>
                        )}
                        {stats.deliveredOrders > 0 && (
                            <div className="bar-item delivered" style={{ width: `${(stats.deliveredOrders / stats.totalOrders) * 100}%` }}>
                                <span>✅ Consegnato ({stats.deliveredOrders})</span>
                            </div>
                        )}
                        {stats.cancelledOrders > 0 && (
                            <div className="bar-item cancelled" style={{ width: `${(stats.cancelledOrders / stats.totalOrders) * 100}%` }}>
                                <span>❌ Cancellato ({stats.cancelledOrders})</span>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Monthly Stats Chart */}
            <div className="stats-section">
                <h3>📅 Andamento Mensile</h3>
                <div className="monthly-chart">
                    <div className="chart-bars">
                        {stats.monthlyStats.map((item, index) => {
                            const maxOrders = Math.max(...stats.monthlyStats.map(m => m.orders));
                            const maxRevenue = Math.max(...stats.monthlyStats.map(m => m.revenue));
                            return (
                                <div key={index} className="chart-column">
                                    <div className="chart-bar-container">
                                        <div 
                                            className="chart-bar orders-bar"
                                            style={{ height: maxOrders > 0 ? `${(item.orders / maxOrders) * 100}%` : '0%' }}
                                        >
                                            <span className="bar-value">{item.orders}</span>
                                        </div>
                                        <div 
                                            className="chart-bar revenue-bar"
                                            style={{ height: maxRevenue > 0 ? `${(item.revenue / maxRevenue) * 100}%` : '0%' }}
                                        >
                                            <span className="bar-value">{formatCurrency(item.revenue)}</span>
                                        </div>
                                    </div>
                                    <div className="chart-label">{item.month}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className="chart-legend">
                        <span className="legend-orders">📦 Ordini</span>
                        <span className="legend-revenue">💰 Fatturato</span>
                    </div>
                </div>
            </div>

            {/* Two columns: Top Products & Recent Orders */}
            <div className="stats-two-columns">
                {/* Top Products */}
                <div className="stats-card">
                    <h3>🏆 Prodotti Più Venduti</h3>
                    <div className="top-products">
                        {stats.topProducts.map((product, index) => (
                            <div key={index} className="product-item">
                                <div className="product-rank">#{index + 1}</div>
                                <div className="product-info">
                                    <div className="product-name">{product.name}</div>
                                    <div className="product-stats">
                                        <span>📦 {product.sales} vendite</span>
                                        <span>💰 {formatCurrency(product.revenue)}</span>
                                    </div>
                                </div>
                                <div className="product-progress">
                                    <div 
                                        className="progress-bar"
                                        style={{ width: stats.topProducts[0]?.sales > 0 ? `${(product.sales / stats.topProducts[0].sales) * 100}%` : '0%' }}
                                    ></div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Recent Orders */}
                <div className="stats-card">
                    <h3>🕐 Ordini Recenti</h3>
                    <div className="recent-orders-list">
                        {stats.recentOrders.map(order => {
                            const statusBadge = getStatusBadge(order.status);
                            return (
                                <div key={order.id} className="recent-order-item">
                                    <div className="order-info">
                                        <div className="order-id">#{order.id}</div>
                                        <div className="order-customer">{order.customer}</div>
                                    </div>
                                    <div className="order-details">
                                        <div className="order-amount">{formatCurrency(order.amount)}</div>
                                        <div 
                                            className="order-status"
                                            style={{ backgroundColor: statusBadge.bg, color: statusBadge.color }}
                                        >
                                            {statusBadge.label}
                                        </div>
                                        <div className="order-date">{order.date}</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>

            {/* Export Section */}
            <div className="export-section">
                <h3>📎 Esporta Report</h3>
                <div className="export-buttons">
                    <button className="export-btn csv" onClick={() => showToast('Esportazione CSV in sviluppo', 'info')}>
                        📄 Esporta CSV
                    </button>
                    <button className="export-btn excel" onClick={() => showToast('Esportazione Excel in sviluppo', 'info')}>
                        📊 Esporta Excel
                    </button>
                    <button className="export-btn pdf" onClick={() => showToast('Esportazione PDF in sviluppo', 'info')}>
                        📑 Esporta PDF
                    </button>
                </div>
            </div>
        </div>
    );
}

export default AdminStats;